import {put, takeEvery} from 'redux-saga/effects';

import PresetApi from '../api/presetApi';
import DeserifyHelper from '../helpers/DeserifyHelper';
import ErrorHelper from '../helpers/errorHelper';
import WindowHelper from '../helpers/windowHelper';
import {ActionTypes} from '../types/actionTypes';
import {Action} from '../typescript/actionTypes';
import {StoreSavedPreset} from '../typescript/storeTypes';

const LOCAL_STORAGE_KEY = 'savedPresets';

function* getPresets(action: Action): Generator<any, any, any> {
    const location = action.payload.location.toLowerCase();

    try {
        yield migrateLocalStoragePresets();
    } catch {
        // A failing migration must never hide the presets that are already in the database.
        // localStorage is kept untouched, so the migration retries on the next load.
    }

    try {
        const presets = yield PresetApi.getPresets(location);
        yield put({type: ActionTypes.GET_PRESETS_SUCCEEDED, payload: presets});
    } catch (e) {
        yield put({type: ActionTypes.GET_PRESETS_FAILED, payload: []});
    }
}

function* savePreset(action: Action): Generator<any, any, any> {
    try {
        const payload = DeserifyHelper.deserify(action.payload);
        const location = payload.location.toLowerCase();
        yield PresetApi.savePreset({
            location: location,
            name: payload.name.toLowerCase(),
            data: payload.data
        });
        const presets = yield PresetApi.getPresets(location);
        yield put({type: ActionTypes.SAVE_PRESET_SUCCEEDED, payload: presets});
    } catch (e) {
        // Without this the save silently fails while the dropdown already shows the new preset.
        ErrorHelper.showMessageNoRethrow(e);
        yield put({type: ActionTypes.SAVE_PRESET_FAILED, payload: []});
    }
}

function* deletePreset(action: Action): Generator<any, any, any> {
    try {
        const location = action.payload.location.toLowerCase();
        yield PresetApi.deletePreset(location, action.payload.name.toLowerCase());
        const presets = yield PresetApi.getPresets(location);
        yield put({type: ActionTypes.DELETE_PRESET_SUCCEEDED, payload: presets});
    } catch (e) {
        // Without this the delete silently fails while the selection is already cleared.
        ErrorHelper.showMessageNoRethrow(e);
        yield put({type: ActionTypes.DELETE_PRESET_FAILED, payload: undefined});
    }
}

// Reads the legacy localStorage presets. Returns null when there is nothing usable to migrate:
// either the key is absent, or its value cannot be read - in which case it is left untouched
// because nothing can be recovered from it anyway.
function readLocalStoragePresets(): StoreSavedPreset[] | null {
    try {
        let raw = WindowHelper.getItemFromLocalStorage(LOCAL_STORAGE_KEY);
        if (raw === null) return null;
        // Some clients stored the value double-encoded, so a single parse yields a string.
        if (typeof raw === 'string') raw = JSON.parse(raw);

        return Array.isArray(raw) ? raw as StoreSavedPreset[] : [];
    } catch {
        return null;
    }
}

function isSamePreset(left: StoreSavedPreset, right: StoreSavedPreset): boolean {
    return left.location.toLowerCase() === right.location.toLowerCase() &&
        left.name.toLowerCase() === right.name.toLowerCase();
}

function* getPresetsForLocations(locations: string[]): Generator<any, StoreSavedPreset[], any> {
    const dbPresets: StoreSavedPreset[] = [];
    for (const location of locations) {
        const presets = yield PresetApi.getPresets(location);
        dbPresets.push(...(presets || []));
    }
    return dbPresets;
}

// One-time migration: push any presets still in localStorage to the database. This is a merge,
// not an overwrite: localStorage is per browser, so a copy left behind in a second browser is
// older than the database and may never win. Only names the database does not have yet are
// pushed. The key is dropped once every local preset is verified present in the database, so a
// failed or partial migration keeps localStorage intact and retries on the next load.
function* migrateLocalStoragePresets(): Generator<any, any, any> {
    const savedPresets = readLocalStoragePresets();
    if (savedPresets === null) return;

    if (!savedPresets.length) {
        WindowHelper.removeItemFromLocalStorage(LOCAL_STORAGE_KEY);
        return;
    }

    const locations = [...new Set(savedPresets.map(p => p.location.toLowerCase()))];
    const dbPresets: StoreSavedPreset[] = yield getPresetsForLocations(locations);

    const missingPresets = savedPresets.filter(local => !dbPresets.some(db => isSamePreset(db, local)));
    if (!missingPresets.length) {
        WindowHelper.removeItemFromLocalStorage(LOCAL_STORAGE_KEY);
        return;
    }

    for (const preset of missingPresets) {
        yield PresetApi.savePreset({
            location: preset.location.toLowerCase(),
            name: preset.name.toLowerCase(),
            data: preset.data
        });
    }

    // Verify the database actually holds every local preset before dropping localStorage.
    const migratedPresets: StoreSavedPreset[] = yield getPresetsForLocations(locations);
    const allMigrated = savedPresets.every(local => migratedPresets.some(db => isSamePreset(db, local)));

    if (allMigrated) {
        WindowHelper.removeItemFromLocalStorage(LOCAL_STORAGE_KEY);
    }
}

function* presetSaga(): Generator<any, any, any> {
    yield takeEvery(ActionTypes.GET_PRESETS_REQUESTED, getPresets);
    yield takeEvery(ActionTypes.SAVE_PRESET_REQUESTED, savePreset);
    yield takeEvery(ActionTypes.DELETE_PRESET_REQUESTED, deletePreset);
}

export default presetSaga;
